import { useState, useEffect } from 'react';
import { Radio, Button, Flex, message } from 'antd';
import { CloseOutlined, SoundOutlined } from '@ant-design/icons';
import { CustomUpload } from '../CustomUpload';
import { AudioDisplay } from './AudioDisplay';
import { useFilesByIds } from '../../context/FileManagementContext';

export const AudioSection = ({
  fileBusinessObjects = [],
  formField,
  handleRemove,
  handleUpload,
}) => {
  const fileIds = fileBusinessObjects.map(obj => obj.fileId);
  const files = useFilesByIds(fileIds);
  const audioFiles = fileBusinessObjects.map(obj => {
    const file = files.find(f => f.id === obj.fileId);
    return { ...obj, ...file };
  });

  const [isRecording, setIsRecording] = useState(false);
  const [audioRecorder, setAudioRecorder] = useState(null);
  const [recordingType, setRecordingType] = useState('upload');
  const [microphoneAvailable, setMicrophoneAvailable] = useState(null);
  const [externalErrors, setExternalErrors] = useState({});

  // 🎯 直接從 DemandContext 獲取檔案
  // const { state } = useDemand();

  // 根據 section 獲取對應的檔案陣列
  // const getFileList = () => {
  //   if (section === 'basic') {
  //     return state.basic?.voiceFile || [];
  //   }
  //   return state.ceremony?.[section]?.files?.my?.audio || [];
  // };

  // const fileList = getFileList();

  // 🔍 檢查麥克風可用性
  const checkMicrophoneAvailability = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMicrophoneAvailable(false);
        return false;
      }

      // 檢查設備列表
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = devices.filter(device => device.kind === 'audioinput');

      if (audioInputDevices.length === 0) {
        setMicrophoneAvailable(false);
        return false;
      }

      setMicrophoneAvailable(true);
      return true;
    } catch (error) {
      console.warn('檢查麥克風可用性時發生錯誤:', error);
      setMicrophoneAvailable(false);
      return false;
    }
  };

  // 🔍 組件載入時檢查麥克風
  useEffect(() => {
    if (recordingType === 'record') {
      checkMicrophoneAvailability();
    }
  }, [recordingType]);

  // 🔍 組件卸載時清理錄音器
  useEffect(() => {
    return () => {
      if (audioRecorder) {
        stopRecording();
      }
    };
  }, [audioRecorder]);

  const startRecording = async () => {
    try {
      console.log('startRecording');
      // 🔍 檢查瀏覽器支援
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('您的瀏覽器不支援錄音功能');
      }

      // 🔍 先檢查麥克風可用性
      const isAvailable = await checkMicrophoneAvailability();
      if (!isAvailable) {
        throw new Error('沒有找到可用的麥克風設備，請檢查：\n1. 是否已連接麥克風\n2. 麥克風是否被其他應用程序使用\n3. 系統音訊設定是否正確');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        try {
          // 停止所有音軌
          stream.getTracks().forEach(track => track.stop());

          if (chunks.length === 0) {
            console.warn('錄音數據為空');
            return;
          }

          const blob = new Blob(chunks, { type: 'audio/webm' });

          // 使用 AudioContext 獲取實際音訊時長
          let audioContext;
          try {
            audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await blob.arrayBuffer();
            const decodedData = await audioContext.decodeAudioData(audioBuffer);
            const actualDuration = decodedData.duration;

            const file = new File([blob], `recording-${Date.now()}.webm`, {
              type: 'audio/webm'
            });

            // 🔄 先清除現有音檔，再上傳新的錄音檔案
            if (audioFiles.length > 0) {
              audioFiles.forEach(audioFile => {
                handleRemove(audioFile.fileId || audioFile.id);
              });
            }

            // 使用標記避免重複檔案檢查
            file._isRecording = true;
            handleUpload({ file, formField });

            // 顯示錄音完成訊息
            message.success('錄音完成並已上傳！');

            // 🔄 錄音完成後切換回上傳音檔模式
            setRecordingType('upload');
          } catch (audioError) {
            console.error('音訊處理錯誤:', audioError);
            // 回退：不計算時長，直接上傳
            const file = new File([blob], `recording-${Date.now()}.webm`, {
              type: 'audio/webm'
            });

            // 🔄 先清除現有音檔，再上傳新的錄音檔案
            if (audioFiles.length > 0) {
              audioFiles.forEach(audioFile => {
                handleRemove(audioFile.fileId || audioFile.id);
              });
            }

            // 使用標記避免重複檔案檢查
            file._isRecording = true;
            handleUpload({ file, formField });

            // 顯示錄音完成訊息
            message.success('錄音完成並已上傳！');

            // 🔄 錄音完成後切換回上傳音檔模式
            setRecordingType('upload');
          } finally {
            if (audioContext && audioContext.state !== 'closed') {
              await audioContext.close();
            }
          }
        } catch (error) {
          console.error('錄音處理錯誤:', error);
          message.error('錄音處理失敗，請重試');
          setIsRecording(false);
          setAudioRecorder(null);
        }
      };

      recorder.onerror = (event) => {
        console.error('錄音錯誤:', event.error);
        setIsRecording(false);
        setAudioRecorder(null);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setAudioRecorder(recorder);
      setIsRecording(true);

      // 顯示錄音開始訊息
      message.success('錄音已開始，請開始說話');
    } catch (err) {
      console.error('開始錄音失敗:', err);
      let errorMessage = '錄音失敗，請稍後再試';

      if (err.name === 'NotAllowedError') {
        errorMessage = '麥克風權限被拒絕，請允許瀏覽器使用麥克風權限';
      } else if (err.name === 'NotFoundError') {
        errorMessage = '沒有找到可用的麥克風設備\n\n請檢查：\n• 是否已連接麥克風\n• 麥克風是否被其他應用程序使用\n• 系統音訊設定是否正確\n• 嘗試重新插拔麥克風';
      } else if (err.name === 'NotReadableError') {
        errorMessage = '麥克風被其他應用程序占用，請關閉其他使用麥克風的程序';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = '麥克風不支援所需的設定，請嘗試使用其他麥克風';
      } else if (err.message) {
        errorMessage = err.message;
      }

      // 顯示錯誤訊息給用戶
      message.error({
        content: errorMessage,
        duration: 5,
        style: {
          whiteSpace: 'pre-line'
        }
      });

      setIsRecording(false);
      setAudioRecorder(null);
      setMicrophoneAvailable(false);
    }
  };

  const stopRecording = () => {
    console.log('stopRecording');
    try {
      if (audioRecorder) {
        // 🔍 檢查錄音器狀態
        if (audioRecorder.state === 'recording') {
          audioRecorder.stop();
        }

        // 🔍 清理音軌資源
        if (audioRecorder.stream) {
          audioRecorder.stream.getTracks().forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
            }
          });
        }
      }
    } catch (error) {
      console.error('停止錄音時發生錯誤:', error);
    } finally {
      // 🔍 確保狀態重置
      const wasRecording = isRecording;
      setIsRecording(false);
      setAudioRecorder(null);

      // 如果之前在錄音，顯示停止訊息
      if (wasRecording) {
        message.info('錄音已停止，正在處理音訊...');
      }
    }
  };

  // 🔄 處理音檔上傳
  const handleAudioFileUpload = (info) => {
    // 🔄 如果是錄音檔案，跳過重複檢查
    if (!info.file._isRecording) {
      const existingFile = audioFiles.find(file =>
        (file.fileId === info.file.uid) ||
        (file.fileName === info.file.name && Math.abs(Date.now() - new Date(file.addedAt || 0).getTime()) < 2000)
      );
      if (existingFile) {
        console.warn('[AudioSection] CustomUpload 檔案已存在，跳過重複上傳:', info.file);
        return;
      }
    }

    if (audioFiles.length > 0) {
      audioFiles.forEach(file => {
        handleRemove(file.fileId || file.id);
      });
    }
    handleUpload({ file: info.file, formField });
  };

  return (
    <Flex vertical className='gap-2'>
      <Radio.Group
        value={recordingType}
        onChange={(e) => setRecordingType(e.target.value)}
        className="w-full"
      >
        <Radio value="upload" className="text-base">上傳音檔</Radio>
        <Radio value="record" className="text-base">一鍵錄音</Radio>
      </Radio.Group>

      {/* 上傳/錄音控件區域 */}
      {recordingType === 'upload' ? (
        <CustomUpload
          type="audio"
          text={audioFiles.length > 0 ? "替換音檔" : "上傳音檔"}
          onChange={handleAudioFileUpload}
        />
      ) : (
        <>
          <Button
            type={isRecording ? "dashed" : "primary"}
            danger={isRecording}
            onClick={() => {
              console.log('Button clicked, isRecording:', isRecording, 'microphoneAvailable:', microphoneAvailable);
              if (isRecording) {
                stopRecording();
              } else {
                startRecording();
              }
            }}
            className="w-full"
            icon={<SoundOutlined />}
            disabled={microphoneAvailable === false}
          >
            {isRecording ? "停止錄音" : "開始錄音"}
          </Button>

          {microphoneAvailable === false && (
            <div className="text-xs text-red-500 mt-1 flex items-center justify-between">
              <span>⚠️ 未檢測到麥克風設備</span>
              <Button
                size="small"
                type="link"
                onClick={checkMicrophoneAvailability}
                className="p-0 h-auto text-xs"
              >
                重新檢測
              </Button>
            </div>
          )}

          {microphoneAvailable === null && recordingType === 'record' && (
            <div className="text-xs text-gray-500 mt-1">
              🔍 正在檢查麥克風設備...
            </div>
          )}
        </>
      )}

      {/* 音檔預覽區域 */}
      {audioFiles.length > 0 && (
        <div className="mt-4">
          {/* 只顯示最新的一個音檔 */}
          {audioFiles.slice(-1).map(file => (
            <div key={file.fileId || file.id} className="relative border border-dashed border-gray-200 p-4 rounded">
              <AudioDisplay file={file} isShowName={true} />
              <CloseOutlined
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 cursor-pointer"
                onClick={() => handleRemove(file.fileId || file.id)}
              />
            </div>
          ))}
        </div>
      )}
      {/* 顯示外部錯誤信息 */}
      {externalErrors.voiceFile && (
        <div className="text-sm text-red-500">
          {externalErrors.voiceFile}
        </div>
      )}
    </Flex>
  );
};